/**
 * [C38] The printed backup recovery sheet (D-173 "QR + words", D-209; D-355): one A4 page under the print root —
 * office, key id, date, what the sheet holds (the whole key, or one share when the platform share is on, D-208),
 * the text in groups of four and the same text as a QR code (error correction M: paper gets folded). Printed through
 * the portal's own print job (no pop-up: the portal's CSP allows no inline style elsewhere); nothing is sent anywhere.
 */
import { qrPath } from "@faylasuf/ui";

export interface BackupSheetProps {
  readonly id: string;
  readonly officeName: string;
  readonly officeCode: string;
  readonly keyId: string;
  readonly text: string;
  readonly createdAt: string;
  readonly t: (k: string, p?: Record<string, string | number>) => string;
  readonly kind: "key" | "share";
}

export function BackupSheet(p: BackupSheetProps) {
  const q = qrPath(p.text, "M");
  const groups = p.text.split("-");
  const rows: string[][] = [];
  for (let i = 0; i < groups.length; i += 4) rows.push(groups.slice(i, i + 4));
  return (
    <div id={p.id} dir="rtl" className="bg-white text-black p-8 font-sans" data-testid="backup-sheet">
      <h1 className="text-xl font-black mb-2">{p.t("maint.sheet.title")}</h1>
      <div className="text-xs leading-7 mb-4">
        <div>
          {p.t("maint.sheet.office")}: {p.officeName} ({p.officeCode})
        </div>
        <div>
          {p.t("maint.sheet.key_id")}: <span dir="ltr" className="font-mono">{p.keyId}</span>
        </div>
        <div>
          {p.t("maint.sheet.date")}: {p.createdAt}
        </div>
      </div>
      <div className="font-black mb-2">{p.kind === "key" ? p.t("maint.sheet.holds_key") : p.t("maint.sheet.holds_share")}</div>
      <div className="border-2 border-black rounded-xl p-4 flex items-center gap-6">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox={`-2 -2 ${q.size + 4} ${q.size + 4}`} width="170" height="170" shapeRendering="crispEdges">
          <rect x={-2} y={-2} width={q.size + 4} height={q.size + 4} fill="#fff" />
          <path d={q.d} fill="#000" />
        </svg>
        <div dir="ltr" className="font-mono text-[17px] leading-9 tracking-wider text-left" data-testid="backup-sheet-text">
          {rows.map((r, i) => (
            <div key={i}>{r.join("  ")}</div>
          ))}
        </div>
      </div>
      <p className="text-xs leading-7 mt-4">{p.kind === "key" ? p.t("maint.sheet.keep_key") : p.t("maint.sheet.keep_share")}</p>
      <p className="text-xs font-black text-rose-800 mt-2">{p.t("maint.sheet.warning")}</p>
    </div>
  );
}
